import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  useTheme
} from '@mui/material';
import { Check } from '@mui/icons-material';

const MultiSelectQuestion = ({ 
  question, 
  value = [], 
  onChange, 
  interpolateText, 
  answers, 
  error 
}) => {
  const theme = useTheme();

  const handleOptionToggle = (optionValue) => {
    const currentValue = Array.isArray(value) ? value : [];
    const newValue = currentValue.includes(optionValue)
      ? currentValue.filter(v => v !== optionValue)
      : [...currentValue, optionValue];
    
    onChange(newValue);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        {interpolateText(question.text, answers)}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select all that apply:
      </Typography>
      
      <Grid container spacing={2}>
        {question.options?.map((option, index) => {
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionLabel = typeof option === 'string' ? option : option.label;
          const isSelected = Array.isArray(value) && value.includes(optionValue);
          
          return (
            <Grid item xs={12} sm={6} md={4} key={optionValue}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  border: isSelected 
                    ? `2px solid ${theme.palette.primary.main}` 
                    : '2px solid transparent',
                  bgcolor: isSelected 
                    ? `${theme.palette.primary.main}10` 
                    : 'background.paper',
                  '&:hover': {
                    bgcolor: isSelected 
                      ? `${theme.palette.primary.main}20` 
                      : `${theme.palette.action.hover}`,
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4]
                  }
                }}
                onClick={() => handleOptionToggle(optionValue)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2, position: 'relative' }}>
                  {isSelected && (
                    <Chip
                      icon={<Check />}
                      label="Selected"
                      size="small"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? 'primary.main' : 'text.primary',
                      mt: isSelected ? 2 : 0
                    }}
                  >
                    {optionLabel}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      
      {/* Show selected items */}
      {Array.isArray(value) && value.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Selected ({value.length}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {value.map((selectedValue) => {
              const option = question.options?.find(opt => 
                (typeof opt === 'string' ? opt : opt.value) === selectedValue
              );
              const label = typeof option === 'string' ? option : option?.label || selectedValue;
              
              return (
                <Chip
                  key={selectedValue}
                  label={label}
                  onDelete={() => handleOptionToggle(selectedValue)}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              );
            })}
          </Box>
        </Box>
      )}
      
      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default MultiSelectQuestion;